/**
 * ActivePlanSafetyBanner — Active-Plan Safety Reassessment V1 UI.
 *
 * Renders a compact banner when the current plan-adaptation decision
 * indicates the athlete's structured health/safety restrictions have
 * changed in a way that affects their currently-active training plan.
 *
 * Two branches:
 *
 *   status === "PLAN_SUSPENDED_FOR_SAFETY"
 *     -> "Your current training plan is paused …" (full suspension)
 *
 *   status === "CHAT2_REASSESSMENT_REQUIRED" with safety_blocked_session_ids
 *     -> "Your training restrictions changed. N upcoming workouts need review."
 *        Each affected session is listed with a concise athlete-friendly
 *        reason (never a diagnosis, never a raw internal code as primary
 *        copy — internal codes appear only as ``data-*`` attributes for
 *        deterministic tests).
 *
 * Any other status (NO_CHANGE_REQUIRED / null / non-safety adaptation)
 * renders nothing.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, PauseCircle } from "lucide-react";

import { fetchCurrentPlanAdaptation } from "@/api/planAdaptationApi";

const STRUCTURED_PREFIX = "STRUCTURED_RESTRICTION:";

// Athlete-friendly copy for each structured restriction code. We keep
// this table small and explicit — no diagnosis language, no
// medicalisation. Unknown codes fall back to a generic phrase.
const CODE_LABEL = {
  NO_RUNNING: "Running is currently restricted.",
  NO_CYCLING: "Cycling is currently restricted.",
  NO_SWIMMING: "Swimming is currently restricted.",
  NO_HIGH_INTENSITY: "High-intensity work is currently restricted.",
  NO_THRESHOLD: "Threshold efforts are currently restricted.",
  NO_VO2: "VO₂ work is currently restricted.",
  NO_ANAEROBIC: "Sprint / anaerobic efforts are currently restricted.",
  NO_SPRINT: "Sprint efforts are currently restricted.",
  NO_LONG_SESSIONS: "Long sessions are currently restricted.",
  MAX_SESSION_DURATION: "Session length is currently limited.",
  TRAINING_SUSPENDED: "Training is currently paused.",
  REQUIRES_CLEARANCE: "Awaiting healthcare clearance.",
};

const codeLabel = (code) =>
  CODE_LABEL[code] || "This workout needs a coaching review.";

// Extract per-session reason codes from the adaptation triggers, in
// stable order.
const perSessionCodes = (decision) => {
  const map = new Map();
  for (const t of decision?.triggers || []) {
    const tc = t?.trigger_code || "";
    if (!tc.startsWith(STRUCTURED_PREFIX)) continue;
    const bare = tc.slice(STRUCTURED_PREFIX.length);
    for (const sid of t?.affected_session_ids || []) {
      const existing = map.get(sid) || [];
      if (!existing.includes(bare)) existing.push(bare);
      map.set(sid, existing);
    }
  }
  return map;
};

export const ActivePlanSafetyBanner = ({
  // Injectable for tests. Defaults to the real API client.
  fetcher = fetchCurrentPlanAdaptation,
}) => {
  const [decision, setDecision] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const ctrl =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetcher({ signal: ctrl?.signal });
        if (!cancelled) setDecision(data || null);
      } catch (_err) {
        // Best-effort surface. Never block the coaching page on this.
        if (!cancelled) setDecision(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
      if (ctrl) ctrl.abort();
    };
  }, [fetcher]);

  if (!ready) return null;
  if (!decision) return null;

  const status = decision.status;
  const blockedIds = decision.safety_blocked_session_ids || [];

  if (status === "PLAN_SUSPENDED_FOR_SAFETY") {
    return (
      <div
        role="alert"
        data-testid="active-plan-safety-banner"
        data-safety-status="PLAN_SUSPENDED_FOR_SAFETY"
        className="flex items-start gap-3 rounded-lg border border-red-700/60 bg-red-900/30 p-4 text-red-100"
      >
        <PauseCircle
          className="mt-0.5 h-5 w-5 shrink-0 text-red-300"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">
            Your current training plan is paused
          </h3>
          <p className="mt-1 text-sm text-red-100/90">
            Your training restrictions changed, so upcoming workouts have been
            put on hold. Review your restrictions or ask your coach to help
            rebuild the plan.
          </p>
        </div>
      </div>
    );
  }

  if (status === "CHAT2_REASSESSMENT_REQUIRED" && blockedIds.length > 0) {
    const codesBySid = perSessionCodes(decision);
    return (
      <div
        role="alert"
        data-testid="active-plan-safety-banner"
        data-safety-status="CHAT2_REASSESSMENT_REQUIRED"
        className="flex flex-col gap-3 rounded-lg border border-amber-700/60 bg-amber-900/30 p-4 text-amber-100"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle
            className="mt-0.5 h-5 w-5 shrink-0 text-amber-300"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold">
              Your training restrictions changed
            </h3>
            <p className="mt-1 text-sm text-amber-100/90">
              {blockedIds.length} upcoming workout
              {blockedIds.length === 1 ? "" : "s"} need
              {blockedIds.length === 1 ? "s" : ""} review before it can run.
            </p>
          </div>
        </div>
        <ul className="space-y-1 pl-8 text-sm">
          {blockedIds.map((sid) => {
            const codes = codesBySid.get(sid) || [];
            const primary = codes[0];
            return (
              <li
                key={sid}
                data-testid={`active-plan-safety-blocked-${sid}`}
                data-block-reason-codes={codes.join(",")}
                data-execution-allowed="false"
                className="rounded border border-amber-800/60 bg-amber-900/20 px-2 py-1"
              >
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  data-testid={`active-plan-safety-action-${sid}`}
                  className="cursor-not-allowed text-left text-amber-100/80"
                >
                  {codeLabel(primary)}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return null;
};

export default ActivePlanSafetyBanner;
